import React, { useState } from "react";
import { X, Plus, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import "./ProductSeoFields.css";

/**
 * Editable SEO / Tags / FAQ fields for the product forms.
 *
 * These are content-only fields populated by the AI Product Assistant (or by
 * hand). They are part of `formData` and submitted with the existing product
 * payload — they never affect SKU, price, stock, GST, shipping or variations.
 *
 * Props:
 *  - formData, setFormData: the parent product form state.
 *  - embedded: when true, renders fields without own collapse (for parent accordion).
 */
const ProductSeoFields = ({ formData, setFormData, embedded = false }) => {
  const [open, setOpen] = useState(false);
  const [keywordInput, setKeywordInput] = useState("");
  const [tagInput, setTagInput] = useState("");

  const metaKeywords = Array.isArray(formData.meta_keywords)
    ? formData.meta_keywords
    : [];
  const tags = Array.isArray(formData.tags) ? formData.tags : [];
  const faqs = Array.isArray(formData.faqs) ? formData.faqs : [];

  const set = (patch) => setFormData((prev) => ({ ...prev, ...patch }));

  const addChip = (key, value, clear) => {
    const v = value.trim();
    if (!v) return;
    const list = Array.isArray(formData[key]) ? formData[key] : [];
    if (list.includes(v)) {
      clear("");
      return;
    }
    set({ [key]: [...list, v] });
    clear("");
  };

  const removeChip = (key, idx) => {
    const list = Array.isArray(formData[key]) ? formData[key] : [];
    set({ [key]: list.filter((_, i) => i !== idx) });
  };

  const updateFaq = (idx, patch) =>
    set({
      faqs: faqs.map((f, i) => (i === idx ? { ...f, ...patch } : f)),
    });

  const removeFaq = (idx) => set({ faqs: faqs.filter((_, i) => i !== idx) });

  const addFaq = () => set({ faqs: [...faqs, { question: "", answer: "" }] });

  const filledCount =
    (formData.meta_title ? 1 : 0) +
    (formData.meta_description ? 1 : 0) +
    metaKeywords.length +
    tags.length +
    faqs.length;

  const fieldsBody = (
    <>
          {/* Meta title */}
          <div className="oms-form-group">
            <label className="oms-form-label">Meta Title</label>
            <input
              type="text"
              className="oms-input"
              placeholder="SEO meta title (≤ 60 characters recommended)"
              value={formData.meta_title || ""}
              onChange={(e) => set({ meta_title: e.target.value })}
            />
          </div>

          {/* Meta description */}
          <div className="oms-form-group">
            <label className="oms-form-label">Meta Description</label>
            <textarea
              className="oms-input"
              rows={3}
              placeholder="SEO meta description (≤ 160 characters recommended)"
              value={formData.meta_description || ""}
              onChange={(e) => set({ meta_description: e.target.value })}
            />
          </div>

          {/* Focus keywords */}
          <div className="oms-form-group">
            <label className="oms-form-label">Focus Keywords</label>
            <div className="psf__tools">
              <input
                type="text"
                className="oms-input"
                placeholder="Add a focus keyword"
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addChip("meta_keywords", keywordInput, setKeywordInput);
                  }
                }}
              />
              <button
                type="button"
                className="oms-add-btn"
                onClick={() =>
                  addChip("meta_keywords", keywordInput, setKeywordInput)
                }
              >
                Add
              </button>
            </div>
            {metaKeywords.length > 0 && (
              <div className="psf__chips">
                {metaKeywords.map((k, i) => (
                  <span key={i} className="psf__chip">
                    {k}
                    <X
                      size={13}
                      onClick={() => removeChip("meta_keywords", i)}
                    />
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Tags */}
          <div className="oms-form-group">
            <label className="oms-form-label">Tags</label>
            <div className="psf__tools">
              <input
                type="text"
                className="oms-input"
                placeholder="Add a tag"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addChip("tags", tagInput, setTagInput);
                  }
                }}
              />
              <button
                type="button"
                className="oms-add-btn"
                onClick={() => addChip("tags", tagInput, setTagInput)}
              >
                Add
              </button>
            </div>
            {tags.length > 0 && (
              <div className="psf__chips">
                {tags.map((t, i) => (
                  <span key={i} className="psf__chip">
                    {t}
                    <X size={13} onClick={() => removeChip("tags", i)} />
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* FAQs */}
          <div className="oms-form-group">
            <div className="psf__faq-head">
              <label className="oms-form-label">FAQs</label>
              <button type="button" className="psf__mini" onClick={addFaq}>
                <Plus size={13} /> Add FAQ
              </button>
            </div>
            {faqs.map((f, i) => (
              <div key={i} className="psf__faq">
                <div className="psf__faq-row">
                  <input
                    type="text"
                    className="oms-input"
                    placeholder="Question"
                    value={f.question || ""}
                    onChange={(e) => updateFaq(i, { question: e.target.value })}
                  />
                  <button
                    type="button"
                    className="psf__del"
                    onClick={() => removeFaq(i)}
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
                <textarea
                  className="oms-input"
                  rows={2}
                  placeholder="Answer"
                  value={f.answer || ""}
                  onChange={(e) => updateFaq(i, { answer: e.target.value })}
                />
              </div>
            ))}
          </div>
    </>
  );

  if (embedded) {
    return (
      <div className="psf psf--embedded">
        <div className="psf__body">{fieldsBody}</div>
      </div>
    );
  }

  return (
    <div className="psf">
      <div className="psf__head" onClick={() => setOpen((o) => !o)}>
        <span className="psf__title">
          SEO, Tags &amp; FAQs
          {filledCount > 0 && <span className="psf__count">{filledCount}</span>}
        </span>
        {open ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
      </div>

      {open && <div className="psf__body">{fieldsBody}</div>}
    </div>
  );
};

export default ProductSeoFields;
